import { DataTableFacetedFilter, type DataTableFacetedFilterOption } from "@/components/table/DataTableFacetedFilter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  RotateCcw,
  X,
} from "lucide-react";
import * as React from "react";

type DataTableFilterType = "text" | "select";

type DataTableFilterOption = {
  label: string;
  value: string;
  count?: number;
  badgeClassName?: string;
};

type DataTableColumnFilter<T> = {
  type?: DataTableFilterType;
  placeholder?: string;
  options?: DataTableFilterOption[];
  getValue?: (row: T) => string | number | boolean | null | undefined;
  multiple?: boolean;
};

type DataTableColumn<T> = {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  align?: "left" | "center" | "right";
  render: (row: T) => React.ReactNode;
  getSortValue?: (row: T) => string | number | null | undefined;
  filter?: DataTableColumnFilter<T>;
};

type DataTableProps<T> = {
  title?: string;
  searchPlaceholder?: string;
  rows: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T, index: number) => string;
  onExportCsv?: () => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  emptyMessage?: string;
};

const TAG_TONES = [
  "border-sky-300 bg-sky-100 text-sky-900",
  "border-violet-300 bg-violet-100 text-violet-900",
  "border-orange-300 bg-orange-100 text-orange-900",
  "border-cyan-300 bg-cyan-100 text-cyan-900",
] as const;

function normalizeText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value).trim().toLowerCase();
}

function pickTagTone(value: unknown): string {
  const normalized = normalizeText(value);
  if (!normalized) return TAG_TONES[0];

  let hash = 0;
  for (const char of normalized) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return TAG_TONES[hash % TAG_TONES.length] ?? TAG_TONES[0];
}

function getComparableValue<T>(row: T, column: DataTableColumn<T>): string | number | boolean {
  const fromFilter = column.filter?.getValue?.(row);
  if (typeof fromFilter === "number" || typeof fromFilter === "string" || typeof fromFilter === "boolean") {
    return fromFilter;
  }

  const fromSort = column.getSortValue?.(row);
  if (typeof fromSort === "number" || typeof fromSort === "string") {
    return fromSort;
  }

  const rendered = column.render(row);
  if (typeof rendered === "number" || typeof rendered === "string" || typeof rendered === "boolean") {
    return rendered;
  }
  return "";
}

function compareValues(a: unknown, b: unknown): number {
  const left = typeof a === "boolean" ? Number(a) : a;
  const right = typeof b === "boolean" ? Number(b) : b;

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  const aText = String(left ?? "");
  const bText = String(right ?? "");

  const aDate = Date.parse(aText);
  const bDate = Date.parse(bText);
  if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
    return aDate - bDate;
  }

  return aText.localeCompare(bText, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

type DataTableColumnMeta<T> = {
  align?: DataTableColumn<T>["align"];
  className?: string;
  headerClassName?: string;
};

function resolveSelectFilterOptions<T>(
  rows: T[],
  column: DataTableColumn<T>,
): DataTableFilterOption[] {
  const counts = new Map<string, number>();
  const labels = new Map<string, string>();

  for (const row of rows) {
    const raw = String(getComparableValue(row, column)).trim();
    if (!raw) continue;

    const normalized = normalizeText(raw);
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    if (!labels.has(normalized)) {
      labels.set(normalized, raw);
    }
  }

  if (column.filter?.options && column.filter.options.length > 0) {
    return column.filter.options
      .map((option) => {
        const count = counts.get(normalizeText(option.value));
        return {
          ...option,
          count,
          badgeClassName: option.badgeClassName ?? pickTagTone(option.value),
        };
      })
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );
  }

  return [...labels.entries()]
    .map(([normalized, label]) => ({
      value: label,
      label,
      count: counts.get(normalized) ?? 0,
      badgeClassName: pickTagTone(label),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
}

const textFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const rowValue = normalizeText(row.getValue(columnId));
  const normalizedFilter = normalizeText(filterValue);
  if (!normalizedFilter) return true;
  return rowValue.includes(normalizedFilter);
};

const selectFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const rowValue = normalizeText(row.getValue(columnId));

  if (Array.isArray(filterValue)) {
    const normalizedFilterValues = filterValue
      .map((value) => normalizeText(value))
      .filter(Boolean);
    if (normalizedFilterValues.length === 0) return true;
    return normalizedFilterValues.includes(rowValue);
  }

  const normalizedFilter = normalizeText(filterValue);
  if (!normalizedFilter) return true;
  return rowValue === normalizedFilter;
};

function sortingButtonClassName(isSorted: false | "asc" | "desc") {
  return cn(
    "inline-flex h-8 items-center gap-1 rounded-md border border-transparent px-2 text-xs transition-colors hover:border-border hover:bg-background/70 hover:text-foreground",
    isSorted ? "text-foreground" : "text-muted-foreground",
  );
}

function SortIcon({ isSorted }: { isSorted: false | "asc" | "desc" }) {
  if (isSorted === "asc") {
    return <ArrowUp className="h-3.5 w-3.5" />;
  }
  if (isSorted === "desc") {
    return <ArrowDown className="h-3.5 w-3.5" />;
  }
  return <ArrowUpDown className="h-3.5 w-3.5" />;
}

function toColumnMeta<T>(column: DataTableColumn<T>): DataTableColumnMeta<T> {
  return {
    align: column.align,
    className: column.className,
    headerClassName: column.headerClassName,
  };
}

function renderCellValue<T>(row: T, column: DataTableColumn<T>) {
  const rendered = column.render(row);

  if (column.filter?.type !== "select") {
    return rendered;
  }

  if (React.isValidElement(rendered)) {
    return rendered;
  }

  if (typeof rendered === "string" || typeof rendered === "number" || typeof rendered === "boolean") {
    return (
      <Badge
        variant="outline"
        className={cn("font-normal", pickTagTone(rendered))}
      >
        {String(rendered)}
      </Badge>
    );
  }

  return rendered;
}

function alignmentClassName(alignment: DataTableColumn<unknown>["align"]): string {
  if (alignment === "center") return "text-center";
  if (alignment === "right") return "text-right";
  return "text-left";
}

type ActiveColumnFilterBadge = {
  id: string;
  columnKey: string;
  columnLabel: string;
  rawValue: string;
  valueLabel: string;
  badgeClassName: string;
  select: boolean;
};

type DataTableViewParams<T> = {
  title?: string;
  searchPlaceholder: string;
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  filterableColumns: DataTableColumn<T>[];
  table: ReturnType<typeof useReactTable<T>>;
  selectFilterOptions: Record<string, DataTableFilterOption[]>;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  onExportCsv?: () => void;
  filteredRowsCount: number;
  activeColumnFilterBadges: ActiveColumnFilterBadge[];
  clearColumnFilterValue: (columnKey: string, rawValue?: string) => void;
  columns: DataTableColumn<T>[];
  emptyMessage: string;
  startRow: number;
  endRow: number;
  currentPage: number;
  totalPages: number;
  currentPageSize: number;
  pageSizeOptions: number[];
};

export function DataTable<T>({
  title,
  searchPlaceholder = "Search...",
  rows,
  columns,
  getRowId,
  onExportCsv,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 50, 100],
  emptyMessage = "No rows found.",
}: DataTableProps<T>) {
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  React.useEffect(() => {
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
      pageSize,
    }));
  }, [pageSize]);

  const filterableColumns = React.useMemo(
    () => columns.filter((column) => column.filter),
    [columns],
  );

  const selectFilterOptions = React.useMemo(() => {
    const optionsByColumn: Record<string, DataTableFilterOption[]> = {};
    for (const column of filterableColumns) {
      if (column.filter?.type !== "select") continue;
      optionsByColumn[column.key] = resolveSelectFilterOptions(rows, column);
    }
    return optionsByColumn;
  }, [filterableColumns, rows]);

  const selectFilterLabels = React.useMemo(() => {
    const labelsByColumn: Record<string, Map<string, DataTableFilterOption>> = {};

    for (const [columnKey, options] of Object.entries(selectFilterOptions)) {
      labelsByColumn[columnKey] = new Map(
        options.map((option) => [normalizeText(option.value), option]),
      );
    }

    return labelsByColumn;
  }, [selectFilterOptions]);

  const searchableColumnIds = React.useMemo(
    () => columns.map((column) => column.key),
    [columns],
  );

  const sortedRows = React.useMemo(() => {
    const activeSort = sorting[0];
    if (!activeSort) {
      return rows;
    }

    const column = columns.find((definition) => definition.key === activeSort.id);
    if (!column || !column.sortable) {
      return rows;
    }

    const nextRows = [...rows];
    nextRows.sort((left, right) => {
      const result = compareValues(
        getComparableValue(left, column),
        getComparableValue(right, column),
      );

      return activeSort.desc ? -result : result;
    });

    return nextRows;
  }, [columns, rows, sorting]);

  const globalFilterFn = React.useCallback<FilterFn<T>>(
    (row, _columnId, filterValue) => {
      const normalizedFilter = normalizeText(filterValue);
      if (!normalizedFilter) return true;
      return searchableColumnIds.some((columnId) =>
        normalizeText(row.getValue(columnId)).includes(normalizedFilter),
      );
    },
    [searchableColumnIds],
  );

  const tableColumns = React.useMemo<ColumnDef<T, unknown>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        accessorFn: (row) => getComparableValue(row, column),
        enableSorting: Boolean(column.sortable),
        sortingFn: (rowA, rowB, columnId) => compareValues(rowA.getValue(columnId), rowB.getValue(columnId)),
        filterFn:
          column.filter?.type === "select"
            ? (selectFilterFn as FilterFn<T>)
            : column.filter
              ? (textFilterFn as FilterFn<T>)
              : undefined,
        meta: toColumnMeta(column),
        header: ({ column: tableColumn }) => {
          if (!column.sortable) {
            return column.label;
          }

          const isSorted = tableColumn.getIsSorted();
          const onToggleSort = tableColumn.getToggleSortingHandler();
          return (
            <button
              type="button"
              className={sortingButtonClassName(isSorted)}
              onClick={onToggleSort}
            >
              {column.label}
              <SortIcon isSorted={isSorted} />
            </button>
          );
        },
        cell: ({ row }) => renderCellValue(row.original, column),
      })),
    [columns],
  );

  const table = useReactTable({
    data: sortedRows,
    columns: tableColumns,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    globalFilterFn,
    getRowId: (row, index) => getRowId(row, index),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: true,
    enableMultiSort: false,
  });

  const hasActiveFilters = globalFilter.trim().length > 0 || columnFilters.length > 0 || sorting.length > 0;

  const clearFilters = React.useCallback(() => {
    setGlobalFilter("");
    setColumnFilters([]);
    setSorting([]);
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, []);

  const clearColumnFilterValue = React.useCallback(
    (columnKey: string, rawValue?: string) => {
      const column = table.getColumn(columnKey);
      if (!column) return;

      const current = column.getFilterValue();
      if (Array.isArray(current)) {
        if (!rawValue) {
          column.setFilterValue(undefined);
          return;
        }

        const next = current.filter(
          (value) => normalizeText(value) !== normalizeText(rawValue),
        );
        column.setFilterValue(next.length > 0 ? next : undefined);
        return;
      }

      column.setFilterValue(undefined);
    },
    [table],
  );

  const filteredRowsCount = table.getFilteredRowModel().rows.length;
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = Math.max(1, table.getPageCount());
  const currentPageSize = table.getState().pagination.pageSize;
  const startRow = filteredRowsCount === 0 ? 0 : (currentPage - 1) * currentPageSize + 1;
  const endRow = filteredRowsCount === 0 ? 0 : Math.min(currentPage * currentPageSize, filteredRowsCount);

  const activeColumnFilterBadges = React.useMemo<ActiveColumnFilterBadge[]>(() => {
    return columnFilters.flatMap((columnFilter) => {
      const definition = columns.find((column) => column.key === columnFilter.id);
      if (!definition) return [];

      const rawValues = Array.isArray(columnFilter.value)
        ? columnFilter.value
        : [columnFilter.value];

      return rawValues
        .map((raw, index) => {
          const value = String(raw ?? "").trim();
          if (!value) return null;

          if (definition.filter?.type === "select") {
            const option = selectFilterLabels[definition.key]?.get(normalizeText(value));
            return {
              id: `${columnFilter.id}-${normalizeText(value)}-${index}`,
              columnKey: definition.key,
              columnLabel: definition.label,
              rawValue: value,
              valueLabel: option?.label ?? value,
              badgeClassName: option?.badgeClassName ?? pickTagTone(value),
              select: true,
            };
          }

          return {
            id: `${columnFilter.id}-${normalizeText(value)}-${index}`,
            columnKey: definition.key,
            columnLabel: definition.label,
            rawValue: value,
            valueLabel: value,
            badgeClassName: "",
            select: false,
          };
        })
        .filter((entry): entry is ActiveColumnFilterBadge => Boolean(entry));
    });
  }, [columnFilters, columns, selectFilterLabels]);

  return renderDataTableView({
    title,
    searchPlaceholder,
    globalFilter,
    setGlobalFilter,
    filterableColumns,
    table,
    selectFilterOptions,
    hasActiveFilters,
    clearFilters,
    onExportCsv,
    filteredRowsCount,
    activeColumnFilterBadges,
    clearColumnFilterValue,
    columns,
    emptyMessage,
    startRow,
    endRow,
    currentPage,
    totalPages,
    currentPageSize,
    pageSizeOptions,
  });
}

function renderDataTableView<T>({
  title,
  searchPlaceholder,
  globalFilter,
  setGlobalFilter,
  filterableColumns,
  table,
  selectFilterOptions,
  hasActiveFilters,
  clearFilters,
  onExportCsv,
  filteredRowsCount,
  activeColumnFilterBadges,
  clearColumnFilterValue,
  columns,
  emptyMessage,
  startRow,
  endRow,
  currentPage,
  totalPages,
  currentPageSize,
  pageSizeOptions,
}: DataTableViewParams<T>) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {title ? <h3 className="text-base font-semibold">{title}</h3> : null}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-2 rounded-lg border bg-muted/20 p-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 w-[220px]"
          />
          {filterableColumns.map((column) => {
            const tableColumn = table.getColumn(column.key);
            if (!tableColumn) return null;

            const filterType = column.filter?.type ?? "text";
            if (filterType === "select") {
              const options = (selectFilterOptions[column.key] ?? []) as DataTableFacetedFilterOption[];
              return (
                <DataTableFacetedFilter
                  key={column.key}
                  column={tableColumn}
                  title={column.label}
                  options={options}
                  multiple={column.filter?.multiple ?? true}
                />
              );
            }

            return (
              <Input
                key={column.key}
                value={String(tableColumn.getFilterValue() ?? "")}
                onChange={(event) => {
                  const next = event.target.value;
                  tableColumn.setFilterValue(next.trim().length > 0 ? next : undefined);
                }}
                placeholder={column.filter?.placeholder ?? column.label}
                className="h-8 w-[170px]"
              />
            );
          })}

          {hasActiveFilters ? (
            <Button type="button" variant="outline" size="sm" className="h-8 border-dashed" onClick={clearFilters}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </Button>
          ) : null}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {onExportCsv ? (
            <Button variant="outline" size="sm" className="h-8" onClick={onExportCsv}>
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </Button>
          ) : null}
          <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
            {filteredRowsCount.toLocaleString()} row{filteredRowsCount === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {globalFilter.trim().length > 0 || activeColumnFilterBadges.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed bg-muted/20 p-2">
          {globalFilter.trim().length > 0 ? (
            <Badge variant="secondary" className="gap-1 rounded-sm pr-1 font-normal">
              Search: {globalFilter.trim()}
              <button
                type="button"
                className="rounded-sm p-0.5 opacity-80 transition-opacity hover:opacity-100"
                onClick={() => setGlobalFilter("")}
                aria-label="Remove global search"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ) : null}

          {activeColumnFilterBadges.map((chip) => (
            <Badge
              key={chip.id}
              variant="outline"
              className={cn(
                "gap-1 rounded-sm pr-1 font-normal",
                chip.select ? chip.badgeClassName : "border-border/80 bg-muted/30 text-foreground",
              )}
            >
              {chip.columnLabel}: {chip.valueLabel}
              <button
                type="button"
                className="rounded-sm p-0.5 opacity-80 transition-opacity hover:opacity-100"
                onClick={() => clearColumnFilterValue(chip.columnKey, chip.rawValue)}
                aria-label={`Remove ${chip.columnLabel} filter`}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border/75 bg-card/70">
        <Table>
          <TableHeader className="bg-muted/35">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as DataTableColumnMeta<T> | undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-12 align-middle font-medium text-muted-foreground",
                        alignmentClassName(meta?.align),
                        meta?.headerClassName,
                        meta?.className,
                      )}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell className="py-10 text-center text-muted-foreground" colSpan={columns.length}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-border/70">
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as DataTableColumnMeta<T> | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "align-top",
                          alignmentClassName(meta?.align),
                          meta?.className,
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col-reverse items-center justify-between gap-3 text-sm text-muted-foreground sm:flex-row">
        <span>
          {startRow}-{endRow} of {filteredRowsCount.toLocaleString()} row{filteredRowsCount === 1 ? "" : "s"}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rows</span>
            <Select
              value={String(currentPageSize)}
              onValueChange={(next) => {
                const nextSize = Number(next);
                if (Number.isFinite(nextSize) && nextSize > 0) {
                  table.setPageSize(nextSize);
                }
              }}
            >
              <SelectTrigger className="h-8 w-[88px]">
                <SelectValue placeholder={currentPageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <span className="text-xs">
            Page {currentPage} of {totalPages}
          </span>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="hidden h-8 w-8 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label="First page"
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="hidden h-8 w-8 lg:flex"
              onClick={() => table.setPageIndex(Math.max(0, table.getPageCount() - 1))}
              disabled={!table.getCanNextPage()}
              aria-label="Last page"
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
